import {toast} from "react-toastify";
import * as XLSX from "xlsx";
import SuccessToastIco from "/public/assets/icons/toast/success.png"
import WarnToastIco from "/public/assets/icons/toast/warn.png"
import ErrorToastIco from "/public/assets/icons/toast/error.png"
import React from "react";
import SparkMD5 from "spark-md5";
import "moment/locale/ko";
import {DEFAULT_ACCEPTS, IMAGE_EXTENSIONS} from "@/components/constants";

/**
 * 토스트 타입
 * @typedef {"success"|"error"|"info"|"warning"} ToastType
 */

/**
 * 파일 업로드 옵션
 * @typedef {object} FileUploadOptions
 * @property {Array<File>} [prevFiles] - 기존 파일 리스트
 * @property {Function} [onAfter] - 업로드 후 실행 함수
 * @property {number} [maxSizeMB] - 최대 파일 용량(MB)
 * @property {boolean} [multiple] - 다중 업로드 여부
 * @property {number} [maxFiles] - 최대 파일 개수
 * @property {string} [type] - 업로드 타입
 * @property {boolean} [imgPreview] - 이미지 미리보기 여부
 * @property {Array<string>} [accepts] - 허용 확장자 리스트
 */


/**
 * Excel 컬럼 정보
 * @typedef {object} ExcelColumn
 * @property {string} key - 데이터 key
 * @property {string} label - 컬럼명
 */

/**
 * 이미지 미리보기 데이터
 * @typedef {object} ImagePreviewMap
 * @property {string} [0]
 * @property {string} [1]
 * @property {string} [2]
 */


let globalUseRouter = null;

/**
 * Next.js App Router 인스턴스를 전역으로 저장합니다.
 * Client Component 내부에서 전달받은 router를
 * store, util 등 React 외부 영역에서 사용할 수 있도록 등록합니다.
 * @param {object} router - next/navigation 의 useRouter() 객체
 */
export const setGlobalUseRouter = (router) => {
    globalUseRouter = router;
};

/**
 * 전역으로 저장된 Next.js router 인스턴스를 반환합니다.
 * 서버 환경에서는 null을 반환하며,
 * router가 아직 초기화되지 않은 경우 경고를 출력 후 null을 반환합니다.
 *
 * @returns {object|null} Next.js router 객체 또는 null
 */
export const getGlobalUseRouter = () => {
    if (typeof window === "undefined") {
        return null;
    }
    if (!globalUseRouter) {
        console.warn("Router is not initialized.");
        return null;
    }
    return globalUseRouter;
};

/**
 * 토스트 메시지 출력
 * @param {ToastType} type - 토스트 타입
 * @param {string} text - 출력 메시지
 * @returns {void}
 */
export const setToast = (type, text) => {
    const icons = {
        success: React.createElement("img", {
            src: SuccessToastIco?.src,
            alt: "success",
            style: {width: 16, height: 16}
        }),
        warning: React.createElement("img", {
            src: WarnToastIco?.src,
            alt: "warning",
            style: {width: 16, height: 18}
        }),
        error: React.createElement("img", {
            src: ErrorToastIco?.src,
            alt: "error",
            style: {width: 16, height: 17}
        }),
    };

    toast(text, {
        position: "top-center",
        type,
        className: `${type}-toast text-left`,
        icon: icons[type] || false,
    });
};

/**
 * 파일 업로드 유틸
 * @param {Event} e - input 이벤트 객체
 * @param {FileUploadOptions} options - 업로드 옵션
 * @returns {Promise<void>}
 */
export async function fileUpload(
    e,
    {
        prevFiles = [],
        onAfter = () => {},
        maxSizeMB = 10,
        multiple = true,
        maxFiles = 3,
        type = "homepage",
        imgPreview = false,
        accepts,
    } = {}
) {
    const input = e?.target;
    const list = Array.from(input?.files || []);
    if (!list.length) return;
    const EXT_LIST = (accepts && accepts.length ? accepts : DEFAULT_ACCEPTS).map(ext => ext.toLowerCase());
    const maxBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : Infinity;
    const valid = [];
    for (const file of list) {
        const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
        const okExt  = EXT_LIST.includes(ext);
        const okSize = file.size <= maxBytes;
        if (!okExt) {
            const msg = `${file.name} 해당 확장자는 업로드 불가 합니다`;
            type === "homepage" ? alert(msg) : setToast("warning", msg);
            continue;
        }
        if (!okSize) {
            const msg = `${file.name} 파일이 ${maxSizeMB}MB를 초과합니다.`;
            type === "homepage" ? alert(msg) : setToast("warning", msg);
            continue;
        }
        const dup = prevFiles.find(path => (path?.name === file.name && path?.size === file.size && path?.lastModified === file.lastModified));
        if (dup) {
            const msg = `${file.name} 이미 추가된 파일입니다.`;
            type === "homepage" ? alert(msg) : setToast("warning", msg);
            continue;
        }
        if (imgPreview) {
            const isImageOk = await checkImageFile(file);
            if (!isImageOk) {
                const msg = `${file.name} 파일을 열 수 없습니다. 다른 이미지를 선택해 주세요.`;
                type === "homepage" ? alert(msg) : setToast("warning", msg);
                continue;
            }
        }
        valid.push(file);
    }
    if (!valid.length) {
        if (input) input.value = "";
        return;
    }
    const remain = Math.max(0, maxFiles - prevFiles.length);
    if (remain <= 0) {
        const msg = `최대 ${maxFiles}개까지 업로드 가능합니다.`;
        type === "homepage" ? alert(msg) : setToast("warning", msg);
        if (input) input.value = "";
        return;
    }
    const nextBatch = valid.slice(0, remain);
    if (valid.length > remain) {
        const msg = `최대 ${maxFiles}개 제한으로 앞에서 ${remain}개만 추가되었습니다.`;
        type === "homepage" ? alert(msg) : setToast("warning", msg);
    }
    const next = multiple ? [...prevFiles, ...nextBatch] : nextBatch;
    onAfter(next);

    if (input) input.value = "";
}

/**
 * 이미지 파일 정상 여부 체크
 * @param {File} file - 이미지 파일
 * @returns {Promise<boolean>}
 */
export const checkImageFile = (file) => {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(true);   // 정상 이미지
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(false);  // 깨진 이미지
        };

        img.src = url;
    });
}

/**
 * 파일 MD5 생성
 * @param {File} file - 파일 객체
 * @returns {Promise<string>}
 */
export async function md5OfFile(file) {
    const buf = await file.arrayBuffer();
    return SparkMD5.ArrayBuffer.hash(buf);
}

/**
 * 게시글 번호 계산 (역순)
 * @param {number} total - 전체 개수
 * @param {number} index - 현재 인덱스
 * @param {number} offset - 페이지 offset
 * @param {number} limit - 페이지 limit
 * @returns {number|undefined}
 */
export const getOrderNumber = (total, index, offset, limit = 10) => {
    if (isNaN(total) || isNaN(index) || isNaN(offset)) return 0;
    const tableListNum = total - (offset * limit) - index;
    if (tableListNum > 0) {
        return tableListNum;
    }
};

/**
 * 게시글 번호 계산 (오름차순)
 * @param {number} index - 현재 인덱스
 * @param {number} offset - 페이지 offset
 * @param {number} limit - 페이지 limit
 * @returns {number}
 */
export const getOrderNumberAsc = (index, offset, limit = 10) => {
    if (isNaN(index) || isNaN(offset)) return 0;
    return offset * limit + index + 1;
};

/**
 * 객체를 QueryString 으로 변환
 * @param {object} params - 변환 객체
 * @returns {string}
 */
export const makeQueryString = (params) => {
    const queryParams = [];
    let queryString = "";
    for (const [key, value] of Object.entries(params)) {
        if (value !== "" && value !== undefined && value !== null) {
            if (key === "selectSearch") {
                for (const [selectKey, selectValue] of Object.entries(value)) {
                    if (selectValue !== "") {
                        queryParams.push(`${selectKey}=${selectValue}`);
                    }
                }
            } else {
                queryParams.push(`${key}=${value}`);
            }
        }
    }
    if (queryParams.length > 0) {
        queryString = `?${queryParams.join("&")}`
    }
    return queryString;
};

/**
 * QueryString 객체 변환
 * @param {URLSearchParams|string} search - search params
 * @returns {object}
 */
export const getQueryParams = (search) => {
    const params = new URLSearchParams(search);
    const result = {};
    for (let [key, value] of params.entries()) {
        result[key] = value;
    }
    return result;
};

/**
 * 검색 파라미터 API 요청 형태로 변환
 * @param {object} params - 검색 파라미터
 * @returns {object}
 */
export const buildSearchQuery = (params) => {
    const queryObject = {};
    for (const [key, value] of Object.entries(params)) {
        if (key === "selectSearch") continue;
        if (value !== "" && value !== undefined && value !== null && typeof value !== "object") {
            queryObject[key] = value;
        }
    }

    if (params["selectSearch"]) {
        const {selectType, selectWord} = params["selectSearch"];
        if (selectType && selectWord) {
            queryObject[selectType] = selectWord;
        }
    }

    return queryObject;
};

/**
 * HTML 제거 후 텍스트 반환
 * @param {string} value - HTML 문자열
 * @returns {string}
 */
export const setHtmlRemovePrint = (value) => {
    if (typeof value !== "string") return "";
    const doc = new DOMParser().parseFromString(value, "text/html");
    return doc.body.textContent || "";
}

/**
 * 첫 번째 이미지 src 추출
 * @param {string} html - HTML 문자열
 * @returns {string|null}
 */
export function getFirstImageSrc(html) {
    if (typeof html !== "string" || !html.trim()) return null;
    if (typeof window !== "undefined" && window.DOMParser) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const images = doc.querySelectorAll("img");
        if (!images.length) return null;
        for (const img of images) {
            const src = img.getAttribute("src");
            if (src && src.trim()) return src.trim();
        }
        return null;
    }
    const match = html.match(/<img[^>]+src=["']?([^"'>\s]+)["']?[^>]*>/i);
    return match?.[1]?.trim() || null;
}

/**
 * 문자열 말줄임 처리
 * @param {string} string - 대상 문자열
 * @param {number} length - 최대 길이
 * @returns {string}
 */
export const ellipsis = (string, length = 80) => (typeof string === "string" && string.length > length ? string.slice(0, length) + "…" : string);

/**
 * 파일 다운로드
 * @param {string} url - 다운로드 URL
 * @param {string} filename - 다운로드 파일명
 * @returns {Promise<void>}
 */
export const downloadFile = async (url, filename) => {
    try {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
    } catch (e) {
        // 폴백: 최후에 그냥 링크 오픈 시도
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "";
        document.body.appendChild(a);
        a.click();
        a.remove();
    }
}

/**
 * 이메일 형식 체크
 * @param {string} value - 이메일 문자열
 * @returns {boolean}
 */
export const isEmail = (value) => {
    if (typeof value !== "string") return false;
    const s = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

/**
 * 이미지 파일 여부 체크
 * @param {string} path - 파일 경로
 * @returns {boolean}
 */
export const isImageFile = (path) => {
    if (!path || typeof path !== "string") return false;
    const cleanPath = path.split("?")[0].split("#")[0];
    const lastDotIndex = cleanPath.lastIndexOf(".");
    if (lastDotIndex === -1) return false;
    const ext = cleanPath.slice(lastDotIndex + 1).toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
}


/**
 * Excel 다운로드 생성
 * @param {Array<object>} data - Excel 데이터
 * @param {Array<ExcelColumn>} columns - 컬럼 데이터
 * @param {string} fileName - 파일명
 * @returns {void}
 */
export const exportToExcelSync = (data, columns, fileName = "export.xlsx") => {
    const updatedAtCol = columns.find(column => column.key === "updatedAt");
    const numCol = columns.find(column => column.key === "num");
    const baseCols = columns.filter(column => column.key !== "updatedAt" && column.key !== "num");
    let orderedColumns;
    if (numCol && updatedAtCol) {
        orderedColumns = [numCol, updatedAtCol, ...baseCols];
    } else if (numCol) {
        orderedColumns = [numCol, ...baseCols];
    } else if (updatedAtCol) {
        orderedColumns = [updatedAtCol, ...baseCols];
    } else {
        orderedColumns = columns;
    }
    const rows = [];
    const linkMeta = [];
    const fileCol = orderedColumns.find((column) => column.key === "file");
    const fileLabel = fileCol ? fileCol.label : null;
    data.forEach((item, rowIndex) => {
        const row = {};
        orderedColumns.forEach((col) => {
            if (col.key === "isActive") {
                row[col.label] = item.isActive === "active" ? "공개" : "비공개";
            } else if (col.key === "file") {
                return;
            } else {
                row[col.label] = item[col.key] ?? "";
            }
        });
        if (fileLabel) {
            const files = Array.isArray(item.files) ? item.files : [];

            if (files.length > 0) {
                const file = files[0]; // 첫 번째 파일만 사용
                const rawName = file?.name || "";
                const normalizedName = rawName.normalize
                    ? rawName.normalize("NFC")
                    : rawName;

                const label = `${fileLabel}1`;
                row[label] = normalizedName;

                linkMeta.push({
                    rowIndex,
                    header: label,
                    target: file.path,
                    tooltip: normalizedName,
                });
            }
        }

        rows.push(row);
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const headerMap = {};
    if (worksheet["!ref"]) {
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let c = range.s.c; c <= range.e.c; c++) {
            const cellRef = XLSX.utils.encode_cell({ c, r: 0 });
            const cell = worksheet[cellRef];
            if (cell && typeof cell.v === "string") {
                headerMap[cell.v] = c;
            }
        }
    }
    linkMeta.forEach((meta) => {
        const colIndex = headerMap[meta.header];
        if (colIndex == null) return;

        const cellRef = XLSX.utils.encode_cell({
            c: colIndex,
            r: meta.rowIndex + 1,
        });

        if (worksheet[cellRef]) {
            worksheet[cellRef].l = {
                Target: meta.target,
                Tooltip: meta.tooltip,
            };
        }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName);
}


/**
 * 이미지 미리보기 데이터 생성
 * @param {Array<File|object>} imgFile - 이미지 파일 리스트
 * @returns {ImagePreviewMap}
 */
export const updateImagePreview= (imgFile) => {
    const files = imgFile || [];
    const next = {};
    for (let i = 0; i < files.length; i++) {
        next[i] = getterPreviewSrc(files[i]);
    }
    return next;
}

/**
 * 이미지 미리보기 URL 반환
 * @param {File|object} item - 파일 객체
 * @returns {string|null}
 */
export const getterPreviewSrc = (item) => {
    if (!item) return null;
    if (item["path"]) return item["path"];
    if (item?.type && typeof item.size === "number") {
        return URL.createObjectURL(item);
    }
    return null;
}






